import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';

const CartItem = ({ item, onUpdateQuantity, onRemove, onMoveToWishlist }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1) return;
    setIsUpdating(true);
    await onUpdateQuantity(item?.id, newQuantity);
    setIsUpdating(false);
  };

  const handleRemove = async () => {
    setIsUpdating(true);
    await onRemove(item?.id);
    setIsUpdating(false);
  };

  const handleMoveToWishlist = async () => {
    setIsUpdating(true);
    await onMoveToWishlist(item?.id);
    setIsUpdating(false);
  };

  return (
    <div className="holographic-card p-6 rounded-lg">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-lg overflow-hidden bg-muted/20">
            <Image
              src={item?.image}
              alt={item?.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Product Details */}
        <div className="flex-1 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="space-y-2">
              <Link 
                to={`/product/${item?.id}`}
                className="text-lg font-semibold text-foreground hover:text-primary transition-colors"
              >
                {item?.name}
              </Link>
              <p className="text-sm text-muted-foreground">{item?.category}</p>
              {item?.variant && (
                <p className="text-sm text-muted-foreground">
                  Variant: {item?.variant}
                </p>
              )}
              
              {/* Stock Status */}
              <div className="flex items-center space-x-2">
                <div className={`inventory-pulse ${item?.stock > 10 ? 'high' : item?.stock > 5 ? 'medium' : 'low'}`}></div>
                <span className="text-sm text-muted-foreground">
                  {item?.stock > 10 ? 'In Stock' : item?.stock > 0 ? `Only ${item?.stock} left` : 'Out of Stock'}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                {formatDZ(item?.price * item?.quantity)}
              </div>
              <div className="text-sm text-muted-foreground">
                {`${formatDZ(item?.price)} each`}
              </div>
              {item?.originalPrice && item?.originalPrice > item?.price && (
                <div className="text-sm text-muted-foreground line-through">
                  {formatDZ(item?.originalPrice)}
                </div>
              )}
            </div>
          </div>

          {/* Quantity and Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Quantity Controls */}
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-muted-foreground">Quantity:</span>
              <div className="flex items-center border border-border rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleQuantityChange(item?.quantity - 1)}
                  disabled={isUpdating || item?.quantity <= 1}
                  className="h-10 w-10 p-0"
                >
                  <Icon name="Minus" size={16} />
                </Button>
                <span className="px-4 py-2 text-center min-w-[3rem] font-medium">
                  {item?.quantity}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleQuantityChange(item?.quantity + 1)}
                  disabled={isUpdating || item?.quantity >= item?.stock}
                  className="h-10 w-10 p-0"
                >
                  <Icon name="Plus" size={16} />
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMoveToWishlist}
                disabled={isUpdating}
                className="text-muted-foreground hover:text-secondary"
              >
                <Icon name="Heart" size={16} className="mr-2" />
                Save for Later
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={isUpdating}
                className="text-muted-foreground hover:text-error"
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Loading Overlay */}
      {isUpdating && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
          <div className="quantum-loader"></div>
        </div>
      )}
    </div>
  );
};

export default CartItem;